import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Avatar, Form, FormInstance } from 'antd';
import clsx from 'clsx';
import Box from 'components/Box';
import Text from 'components/Text';
import moment from 'moment';
import { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';
import avatar_image from './avatar.png';
import clock_icon from './clock-icon.png';
import { AptStatusHandler } from '../../Staff';
import { IQuickBookingV2ServiceItem } from '../../type';
import Icon from 'assets/Icons';
import anybodyImg from 'assets/icon-anybody.png';

export type QuickBookingV2ServiceItemProps = {
  index?: number;
  value?: IQuickBookingV2ServiceItem;
  onRemove?: () => void;
  onActiveService?: (value: IQuickBookingV2ServiceItem, services?: IQuickBookingV2ServiceItem[]) => void;
  onOpenChangeStaff?: QuickBookingV2ServiceItemProps['onActiveService'];
  form?: FormInstance<any>;
  onChange?: (value: IQuickBookingV2ServiceItem) => void;
  onChangeTime?: (value: IQuickBookingV2ServiceItem) => void;
  drag?: boolean;
  isBookByService?: boolean;
  name?: any;
};
const QuickBookingV2ServiceItem = ({ name, form, index = 0, value, onRemove, onActiveService, onChange, onChangeTime, drag, isBookByService, onOpenChangeStaff }: QuickBookingV2ServiceItemProps) => {
  const activeServiceId = Form.useWatch(['activeServiceId'], form);
  const duration = useMemo(() => value?.masterDuration ? value?.masterDuration : (value?.duration || 0), [value]);
  const startTime = useMemo(() => value?.masterStartTime ? value?.masterStartTime : value?.startTime, [value]);
  const endTime = useMemo(() => {
    return moment(startTime || '', 'hh:mm A').add(duration, 'minutes').format('hh:mm A');
  }, [startTime, duration]);
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({ id: value?.id || '' });

  const isChangeStaff = !!onOpenChangeStaff;

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    display: 'flex',
    flex: 1,
    transition
  };

  const handleChangeStaff: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isChangeStaff || !value) return;
    e.stopPropagation();
    onOpenChangeStaff?.(value);
  };

  const renderStaff = () => {
    if (value?.staffId)
      return (
        <Box onClick={handleChangeStaff} display='flex' alignItems='center' gap={isBookByService ? '2' : '4'}>
          <CheckBoxRequest>
            <Form.Item noStyle name={[name, 'aptStatus']}>
              <AptStatusHandler icon />
            </Form.Item>
          </CheckBoxRequest>
          {
            value.anybody ?
              <img src={anybodyImg} alt="anybodyImg" style={{ width: isBookByService ? 40 : 56, height: isBookByService ? 40 : 56, borderRadius: isBookByService ? 40 : 56, }} /> :
              <Avatar src={value?.staffAvatar} size={isBookByService ? 40 : 56}>{value?.staffName?.[0]}</Avatar>
          }
          <Text className='text-staff-name' style={{ whiteSpace: 'nowrap', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value?.staffName || '--'}</Text>
          {isChangeStaff && <div style={{ zoom: 1.5 }}>
            <Icon type="changeCustomer" fill='#6FABB6' />
          </div>}
        </Box>
      );
    return (
      <Box onClick={handleChangeStaff} display='flex' alignItems='center' gap='2'>
        <CheckBoxRequest>
          <Form.Item noStyle name={[name, 'aptStatus']}>
            <AptStatusHandler icon />
          </Form.Item>
        </CheckBoxRequest>
        <img src={avatar_image} alt="" style={{ width: isBookByService ? 40 : 56, height: isBookByService ? 40 : 56 }} />
        <Text className='text-select-staff'>Select staff</Text>
      </Box>
    );
  };
  if (!value) return null;

  return (
    <>
      <Container {...attributes} {...listeners} ref={setActivatorNodeRef} type='button' onClick={() => onActiveService?.(value)} className={clsx(activeServiceId === value?.id && 'active', drag && 'drag')} isBookByService={isBookByService}>
        <svg viewBox="0 0 20 20" width="12">
          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
        </svg>
        <div ref={setNodeRef} style={style}>
          <Box display='flex' flexDirection='column' style={{ flex: 1 }}>
            <Box display='flex' alignItems='center' gap='2'>
              <Text className='service-name'>{index + 1}. {value?.serviceName || '--'}</Text>
              <Text className='duration-text'>{duration ? `(${duration}m)` : ''}</Text>
            </Box>
            <Box display='flex' alignItems='center' gap='2'>
              <img src={clock_icon} alt="clock_icon" style={{ width: 24, height: 24 }} />
              <button type='button' onClick={() => value && onChangeTime?.(value)}>
                <Text className='duration'>{startTime || '--'} - {endTime || '--'}</Text>
              </button>
            </Box>
          </Box>
          <Box style={{ flex: 1, justifyContent: 'flex-end' }} display='flex' ml={isBookByService ? '2' : '4'} gap={isBookByService ? '1' : '4'} alignItems='center'>
            {renderStaff()}
            <button className='btn-clear' type='button' onClick={e => [e.stopPropagation(), onRemove?.()]}><IconRemove /></button>
          </Box>
        </div>
      </Container>
    </>
  );
};

export { QuickBookingV2ServiceItem };

const Container = styled.button<{ isBookByService?: boolean }>`
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 4px 8px;
  gap: 16px;

  border-radius: 4px;
  border: 2px solid #86909C;
  background: #FFF;
  box-shadow: 4px 4px 16px 4px #FFF;

  .service-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    text-align: left;
    color: #1D2129;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: ${props => props.isBookByService ? '16px' : '20px'};
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .duration-text {
    text-align: left;
    color: #D2464F;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: ${props => props.isBookByService ? '16px' : '20px'};
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .duration {
    overflow: hidden;
    color: #4384FF;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: Poppins;
    font-size: ${props => props.isBookByService ? '16px' : '20px'};
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .text-select-staff {
    color: #4384FF;
    font-family: Poppins;
    font-size: ${props => props.isBookByService ? '16px' : '20px'};
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .text-staff-name {
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: ${props => props.isBookByService ? '16px' : '20px'};
    font-style: normal;
    font-weight: 600;
    line-height: ${props => props.isBookByService ? '24px' : '30px'};
    text-transform: uppercase;
  }

  .btn-clear {
    display: flex;
    width: 48px;
    height: 32px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    aspect-ratio: 1/1;
  }

  &.active {
    border-radius: 4px;
    border: 2px solid #D2464F;
    background: #FFF;
    box-shadow: 4px 4px 16px 4px rgba(210, 70, 79, 0.15);
  }
  &.drag {
    width: calc(85vw - 32px - 650px - 700px);
  }
`;
const IconRemove = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M16.8186 3.18089C17.1952 3.55748 17.1952 4.16805 16.8186 4.54464L4.54491 16.8183C4.16833 17.1949 3.55776 17.1949 3.18117 16.8183C2.80458 16.4417 2.80459 15.8312 3.18117 15.4546L15.4548 3.18089C15.8314 2.80431 16.442 2.80431 16.8186 3.18089Z" fill="#DA072D" />
  <path fillRule="evenodd" clipRule="evenodd" d="M3.18141 3.18089C3.558 2.80431 4.16857 2.80431 4.54515 3.18089L16.8188 15.4546C17.1954 15.8312 17.1954 16.4417 16.8188 16.8183C16.4422 17.1949 15.8317 17.1949 15.4551 16.8183L3.18141 4.54464C2.80482 4.16805 2.80482 3.55748 3.18141 3.18089Z" fill="#DA072D" />
</svg>;

const CheckBoxRequest = styled.label`
  zoom: 1.2;
  user-select: none;
  &:active {
    background: #eee;
  }
  .checkbox-container span {
    color: #4384ff;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 30px; /* 166.667% */
    text-transform: capitalize;
  }
`;
